import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import axios from 'axios';
import { environment } from '../../../environments/environment';

export interface CartItem {
    id: string;
    name: string;
    category?: string;
    price: number;
    priceFormatted?: string;
    qty: number;
    image: string;
    description?: string;
    selectedOption?: string;
    selectedOptionId?: string;
    selectedAddons?: { name: string; price: number }[];
    itemable_type?: string;
    itemable_id?: string;
    custom_options?: any;
}

@Injectable({
    providedIn: 'root',
})
export class CartService {
    private readonly STORAGE_KEY = 'visidea_cart_items';
    private readonly STORAGE_TOKEN_KEY = 'visidea_cart_token';

    private itemsSubject = new BehaviorSubject<CartItem[]>(this.loadInitialItems());
    items$ = this.itemsSubject.asObservable();

    private itemAddedSubject = new Subject<CartItem>();
    itemAdded$ = this.itemAddedSubject.asObservable();

    private isCartOpenSubject = new BehaviorSubject<boolean>(false);
    isCartOpen$ = this.isCartOpenSubject.asObservable();

    private isLoadingSubject = new BehaviorSubject<boolean>(false);
    isLoading$ = this.isLoadingSubject.asObservable();

    private directCheckoutSubject = new Subject<void>();
    directCheckout$ = this.directCheckoutSubject.asObservable();
    private directCheckoutContext: { cartToken: string; items: CartItem[] } | null = null;

    constructor() {
        // Sync items to localStorage whenever they change locally as backup
        this.items$.subscribe((items) => {
            try {
                localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
            } catch (e) {
                console.error('Error saving cart to localStorage', e);
            }
        });

        // Initialize cart from backend API on startup
        this.fetchCartFromApi();
    }

    /** ===================== 🔹 CART TOKEN MANAGEMENT 🔹 ===================== */
    public getCartToken(): string {
        let token: string | null = null;
        try {
            token = localStorage.getItem(this.STORAGE_TOKEN_KEY);
        } catch (e) {
            console.warn('Could not read cart token from localStorage:', e);
        }

        if (!token || token === '111111') {
            token = this.generateUniqueToken();
            try {
                localStorage.setItem(this.STORAGE_TOKEN_KEY, token);
            } catch (e) {
                console.warn('Could not write cart token to localStorage:', e);
            }
        }
        return token;
    }

    private generateUniqueToken(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return 'cart_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 9);
    }

    public setCartToken(token: string): void {
        if (token) {
            localStorage.setItem(this.STORAGE_TOKEN_KEY, token);
        }
    }

    private getApiUrl(path: string): string {
        const baseUrl = environment.baseurl_api || 'https://dev.api.visideastudio.com';
        const cleanBase = baseUrl.replace(/\/+$/, '');
        const cleanPath = path.replace(/^\/+/, '');
        return `${cleanBase}/${cleanPath}`;
    }

    private getHeaders() {
        return {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        };
    }

    private loadInitialItems(): CartItem[] {
        try {
            const saved = localStorage.getItem(this.STORAGE_KEY);
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (e) {
            console.error('Error loading cart from localStorage', e);
        }
        return [];
    }

    get items(): CartItem[] {
        return this.itemsSubject.value;
    }

    updateItems(items: CartItem[]): void {
        this.itemsSubject.next(items);
    }

    /** ===================== 🔹 BACKEND API INTEGRATION 🔹 ===================== */

    async fetchCartFromApi(): Promise<void> {
        // Disabled: Pure LocalStorage mode
    }

    async addItem(item: Omit<CartItem, 'qty'> & { qty?: number }, quantity: number = 1, notify = true): Promise<void> {
        // Optimistic update locally
        const currentItems = [...this.items];
        const existingIndex = currentItems.findIndex((i) => i.id === item.id);

        if (existingIndex > -1) {
            currentItems[existingIndex] = {
                ...currentItems[existingIndex],
                qty: currentItems[existingIndex].qty + (quantity || 1),
            };
        } else {
            currentItems.push({
                ...item,
                qty: quantity || 1,
            });
        }
        this.updateItems(currentItems);
        const addedItem = currentItems.find((currentItem) => currentItem.id === item.id)!;
        if (notify) {
            this.itemAddedSubject.next(addedItem);
        }

        // Disabled backend sync
    }

    async startDirectCheckout(item: Omit<CartItem, 'qty'> & { qty?: number }, quantity: number = 1): Promise<void> {
        if (this.directCheckoutContext) {
            this.finishDirectCheckout();
        }

        this.directCheckoutContext = {
            cartToken: this.getCartToken(),
            items: [...this.items],
        };
        this.setCartToken(this.generateUniqueToken());
        this.updateItems([]);

        await this.addItem(item, quantity, false);
        this.directCheckoutSubject.next();
    }

    finishDirectCheckout(): void {
        if (!this.directCheckoutContext) {
            return;
        }

        const context = this.directCheckoutContext;
        this.directCheckoutContext = null;
        this.setCartToken(context.cartToken);
        this.updateItems(context.items);
    }

    async updateQty(id: string, qty: number): Promise<void> {
        if (qty <= 0) {
            await this.removeItem(id);
            return;
        }

        // Optimistic local update
        const currentItems = this.items.map((item) => {
            if (item.id === id) {
                return { ...item, qty };
            }
            return item;
        });
        this.updateItems(currentItems);
    }

    async removeItem(id: string): Promise<void> {
        // Optimistic local update
        const currentItems = this.items.filter((item) => item.id !== id);
        this.updateItems(currentItems);
    }

    async clearCart(): Promise<void> {
        this.updateItems([]);
    }

    async checkout(data: {
        cart_id?: string;
        name: string;
        email: string;
        phone: string;
        event_date?: string;
        event_location?: string;
        booking_type: 'FULL_PAYMENT' | 'DOWN_PAYMENT' | 'QUOTATION';
        dp_percentage?: number;
        payment_channel_id?: string;
        notes?: string;
        discount_code?: string;
        custom_domain?: string;
    }): Promise<any> {
        const url = this.getApiUrl('/api/v1/front/public/checkout');
        const defaultChannelId = environment.qris_payment_channel_id || '019e86da-b3c4-7256-bbaf-8939633358c5';

        // Map local items to direct_items format asynchronously
        const directItems = await Promise.all(this.items.map(async (item) => {
            const rawType = item.itemable_type || this.resolveItemableType(item.category || item.name);
            const preferredId = item.itemable_id || (this.isUuid(item.id) ? item.id : undefined);
            
            const validItemableId = preferredId && this.isUuid(preferredId) ? preferredId : await this.getValidPackageUuid(rawType, item.name || item.category);

            const customOptions: any = item.custom_options || {
                themed_product_id: item.selectedOptionId || (this.isUuid(item.id) ? item.id : null),
                themed_product_name: item.selectedOption || item.name,
            };

            if (data.custom_domain && (item.category?.toLowerCase().includes('ticket') || item.name?.toLowerCase().includes('ticket') || item.category?.toLowerCase().includes('undangan') || item.name?.toLowerCase().includes('undangan'))) {
                customOptions.custom_domain = data.custom_domain;
            }

            return {
                itemable_type: rawType,
                itemable_id: validItemableId,
                quantity: item.qty || 1,
                custom_options: customOptions
            };
        }));

        const payload: any = {
            direct_items: directItems,
            name: data.name,
            email: data.email,
            phone: data.phone,
            event_date: data.event_date || null,
            event_location: data.event_location || null,
            booking_type: data.booking_type,
            notes: data.notes || null,
        };

        if (data.booking_type === 'DOWN_PAYMENT') {
            payload.dp_percentage = data.dp_percentage || 50;
        }

        if (data.booking_type !== 'QUOTATION') {
            payload.payment_channel_id = data.payment_channel_id || defaultChannelId;
        }

        if (data.discount_code) {
            payload.discount_code = data.discount_code;
        }

        try {
            const res = await axios.post(url, payload, this.getHeaders());
            if (res.data && res.data.success) {
                if (this.directCheckoutContext) {
                    this.finishDirectCheckout();
                } else {
                    this.updateItems([]);
                }
                return res.data;
            } else {
                throw new Error(res.data?.message || 'Checkout gagal diproses.');
            }
        } catch (err: any) {
            console.error('API checkout error:', err);
            throw err;
        }
    }


    /** ===================== 🔹 HELPERS & UTILS 🔹 ===================== */

    public isUuid(str?: string): boolean {
        if (!str || typeof str !== 'string') return false;
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str.trim());
    }

    private packageCache: { [key: string]: any[] } = {};

    private async getValidPackageUuid(itemableType: string, searchKey?: string): Promise<string | null> {
        const normType = this.resolveItemableType(itemableType);

        if (searchKey && this.isUuid(searchKey)) {
            return searchKey;
        }

        try {
            let endpoint = '';
            if (normType === 'ThemedProductPackage') {
                endpoint = '/api/v1/front/public/themed-product-packages/list?paginate=false';
            } else if (normType === 'NonThemedProductPackage') {
                endpoint = '/api/v1/front/public/non-themed-product-packages/list?paginate=false';
            } else {
                endpoint = '/api/v1/front/public/bundling-packages/list?paginate=false';
            }

            if (!this.packageCache[normType]) {
                const url = this.getApiUrl(endpoint);
                const res = await axios.get(url, this.getHeaders());
                let list: any[] = [];
                if (res.data && res.data.success && res.data.data) {
                    list = Array.isArray(res.data.data.list) ? res.data.data.list : (Array.isArray(res.data.data) ? res.data.data : []);
                } else if (Array.isArray(res.data)) {
                    list = res.data;
                }
                this.packageCache[normType] = list;
            }

            const list = this.packageCache[normType] || [];
            if (list.length > 0) {
                if (searchKey) {
                    const searchLower = searchKey.toLowerCase().trim();
                    const matched = list.find(p => p.id && this.isUuid(p.id) && (
                        String(p.name || '').toLowerCase().includes(searchLower) ||
                        searchLower.includes(String(p.name || '').toLowerCase())
                    ));
                    if (matched) return matched.id;
                }
                const firstUuid = list.find(p => p.id && this.isUuid(p.id));
                if (firstUuid) return firstUuid.id;
            }
        } catch (e) {
            console.warn('Package UUID resolution warning:', e);
        }
        return null;
    }

    private resolveItemableType(categoryOrName: string): string {
        const lower = (categoryOrName || '').toLowerCase();
        if (lower.includes('non') || lower.includes('non-themed')) {
            return 'NonThemedProductPackage';
        } else if (lower.includes('themed') || lower.includes('tema') || lower.includes('undangan')) {
            return 'ThemedProductPackage';
        } else if (lower.includes('bundling') || lower.includes('paket') || lower.includes('package')) {
            return 'BundlingPackage';
        }
        return 'NonThemedProductPackage';
    }

    private mapApiItemToCartItem(apiItem: any): CartItem {
        const itemable = apiItem.itemable || {};
        const customOptions = apiItem.custom_options || {};
        
        const price = apiItem.unit_price || itemable.price || 0;
        
        let name = itemable.name || 'Produk Visidea';
        let category = apiItem.itemable_type ? apiItem.itemable_type.split('\\').pop() : 'Paket';

        if (customOptions.themed_product_name) {
            name = customOptions.themed_product_name;
            category = `Undangan Digital${itemable.name ? ' (' + String(itemable.name).toUpperCase() + ')' : ''}`;
        } else if (itemable.name) {
            name = itemable.name;
        }

        if (customOptions.cart_name) {
            name = customOptions.cart_name;
        }
        if (customOptions.category) {
            category = customOptions.category;
        }

        const image = customOptions.themed_product_image || customOptions.image_url || customOptions.image || itemable.image || itemable.cover_image || itemable.cover_image_url || '/assets/visidea/banner_slide.jpg';

        return {
            id: apiItem.id, // CartItem ID in backend DB
            itemable_id: apiItem.itemable_id,
            itemable_type: apiItem.itemable_type,
            name: name,
            category: category,
            price: price,
            priceFormatted: this.formatCurrency(price),
            qty: apiItem.quantity || 1,
            image: image,
            description: customOptions.description || itemable.description || '',
            selectedOption: customOptions.selected_option || (customOptions.themed_product_name ? `Paket: ${itemable.name || 'Standard'}` : undefined),
            custom_options: customOptions,
        };
    }

    get totalQty(): number {
        return this.items.reduce((sum, item) => sum + (item.qty || 0), 0);
    }

    get totalAmount(): number {
        return this.items.reduce((sum, item) => sum + (item.price * (item.qty || 1)), 0);
    }

    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    }

    openCart(): void {
        this.isCartOpenSubject.next(true);
    }

    closeCart(): void {
        this.isCartOpenSubject.next(false);
    }

    toggleCart(): void {
        this.isCartOpenSubject.next(!this.isCartOpenSubject.value);
    }
}

